import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Save } from "lucide-react";
import { toast } from "sonner";

// Owner-only: edit default or per-lot T&C. Every save creates a new version (old ones kept).
export default function TermsSettings() {
  const [versions, setVersions] = useState([]);
  const [lots, setLots] = useState([]);
  const [scope, setScope] = useState("all");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [{ data: v }, { data: l }] = await Promise.all([api.get("/terms/versions"), api.get("/lots?includeInactive=true")]);
    setVersions(v);
    setLots(l);
  };
  useEffect(() => { load(); }, []);

  const scoped = versions.filter((v) => v.lotId === scope);
  const fallback = versions.find((v) => v.lotId === "all");
  useEffect(() => { setText((scoped[0] || fallback)?.bodyText || ""); }, [scope, versions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/terms/versions", { lotId: scope, bodyText: text });
      toast.success(`Saved Terms v${data.version}${scope === "all" ? " (default)" : ""}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="terms-settings" className="border-slate-800 bg-slate-900 p-6 lg:col-span-2">
      <div className="mb-1 flex items-center gap-2">
        <FileText className="h-5 w-5 text-[#d946ef]" />
        <h2 className="font-heading text-xl font-bold text-slate-100">Terms &amp; Conditions</h2>
      </div>
      <p className="mb-4 text-sm text-slate-400">
        Drivers sign the current version at every check-in. Saving creates a new version — past signatures keep pointing to the exact text they agreed to.
      </p>
      <div className="mb-3 w-72">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="terms-scope-select" className="h-11 border-slate-700 bg-slate-950"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="terms-scope-all">All lots (default)</SelectItem>
            {lots.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`terms-scope-${l.id}`}>{l.name} (override)</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {scope !== "all" && !scoped.length && (
        <p data-testid="terms-using-default" className="mb-2 text-xs text-amber-300">This lot uses the default terms. Saving here creates a lot-specific override.</p>
      )}
      <textarea data-testid="terms-body-input" value={text} onChange={(e) => setText(e.target.value)} rows={14}
        className="w-full rounded-md border border-slate-700 bg-slate-950 p-3 font-data text-xs leading-relaxed text-slate-200" />
      <div className="mt-3 flex items-center gap-4">
        <Button data-testid="terms-save" onClick={save} disabled={saving || text.trim().length < 20} className="h-11 gap-2 bg-[#A0148C] font-bold hover:bg-[#80106F]">
          <Save className="h-4 w-4" /> Save as new version
        </Button>
        <p data-testid="terms-version-history" className="text-xs text-slate-500">
          {scoped.length ? `Versions: ${scoped.map((v) => `v${v.version} (${new Date(v.createdAt).toLocaleDateString()})`).join(", ")}` : "No versions for this scope yet"}
        </p>
      </div>
    </Card>
  );
}
